// Designer modal to define a column's footer total. Two modes:
//   - Aggregate: pick SUM / AVG / MIN / MAX / COUNT from a dropdown.
//   - Expression: free-form formula. Live-evaluates against current rows
//                 (when provided) so the designer sees the result.

import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Save, Trash2, X } from 'lucide-react'

import type { ColumnDef } from '../types'
import type {
  EmbedColumnAggregate, EmbedColumnTotal, EmbedColumnTotalKind,
} from '@twasta/page-schema'
import { evaluateExpression, applyAggregate } from './expressionEval'
import { listHeadingOf } from '../utils'

interface Props {
  open: boolean
  column: ColumnDef
  columnKey: string
  columns: ColumnDef[]            // all columns — used to surface
  columnKeyOf: (c: ColumnDef) => string
  current: EmbedColumnTotal | null
  // Preview rows; pass [] when no data is available yet.
  previewRows: Array<Record<string, unknown>>
  onSave: (next: EmbedColumnTotal) => void
  onClear: () => void
  onClose: () => void
}

const AGG_OPTIONS: { value: EmbedColumnAggregate; label: string }[] = [
  { value: 'sum', label: 'SUM (total)' },
  { value: 'avg', label: 'AVG (average)' },
  { value: 'min', label: 'MIN' },
  { value: 'max', label: 'MAX' },
  { value: 'count', label: 'COUNT' },
]

export default function ColumnTotalEditor({
  open, column, columnKey, columns, columnKeyOf, current, previewRows,
  onSave, onClear, onClose,
}: Props) {
  const [kind, setKind] = useState<EmbedColumnTotalKind>(current?.kind ?? 'aggregate')
  const [agg, setAgg] = useState<EmbedColumnAggregate>(() => {
    if (current?.kind === 'aggregate') {
      const v = (current.spec || '').toLowerCase() as EmbedColumnAggregate
      if (['sum', 'avg', 'min', 'max', 'count'].includes(v)) return v
    }
    return 'sum'
  })
  const [expr, setExpr] = useState<string>(
    current?.kind === 'expression' ? current.spec : '',
  )
  const [label, setLabel] = useState<string>(current?.label ?? '')

  const columnDbNames = useMemo(() => columns.map(columnKeyOf), [columns, columnKeyOf])

  const preview = useMemo(() => {
    if (kind === 'aggregate') {
      const v = applyAggregate(previewRows, columnKey, agg)
      return { ok: true, display: String(v) }
    }
    if (!expr.trim()) return { ok: false, display: 'Enter a formula above' }
    const res = evaluateExpression(expr, previewRows, columnDbNames)
    if (!res.ok) return { ok: false, display: `Error: ${res.error}` }
    return { ok: true, display: String(res.result) }
  }, [kind, agg, expr, previewRows, columnKey, columnDbNames])

  if (!open) return null

  const node = (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 2300,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-bg)', color: 'var(--color-text)',
          border: '1px solid var(--color-border)', borderRadius: 10,
          width: 'min(560px, 96vw)',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
          overflow: 'hidden',
        }}
      >
        <div style={{
          padding: '12px 18px', borderBottom: '1px solid var(--color-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <strong style={{ fontSize: '0.9625rem' }}>Column total</strong>
            <div style={{
              fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
            }}>
              {listHeadingOf(column)}
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} title="Close">
            <X size={14} />
          </button>
        </div>

        <div style={{
          padding: 18, display: 'flex', flexDirection: 'column', gap: 12,
          fontSize: '0.8825rem',
        }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="radio"
                name="total-kind"
                checked={kind === 'aggregate'}
                onChange={() => setKind('aggregate')}
              />
              <span>Aggregate</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="radio"
                name="total-kind"
                checked={kind === 'expression'}
                onChange={() => setKind('expression')}
              />
              <span>Expression</span>
            </label>
          </div>

          {kind === 'aggregate' ? (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={labelStyle}>Function</span>
              <select
                value={agg}
                onChange={(e) => setAgg(e.target.value as EmbedColumnAggregate)}
                style={inputStyle}
              >
                {AGG_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
          ) : (
            <>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={labelStyle}>Formula</span>
                <textarea
                  value={expr}
                  onChange={(e) => setExpr(e.target.value)}
                  placeholder="e.g. ROUND(qty * 1.18, 2)  ·  qty - returned  ·  IF(qty_count = 0, 0, sum / qty_count)"
                  rows={3}
                  style={{
                    ...inputStyle,
                    fontFamily: 'var(--font-mono, monospace)',
                    resize: 'vertical',
                  }}
                />
              </label>
              <details style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                <summary style={{ cursor: 'pointer' }}>Formula reference</summary>
                <div style={{ padding: '6px 0 0 0', lineHeight: 1.5 }}>
                  <p style={{ margin: '4px 0' }}>
                    <strong>Column references</strong> use the column's db_name.
                    A bare reference is the column's SUM:
                  </p>
                  <ul style={{ margin: '0 0 6px 18px', padding: 0 }}>
                    <li><code>qty</code> → SUM(qty)</li>
                    <li><code>qty_sum</code> · <code>qty_avg</code> · <code>qty_min</code> · <code>qty_max</code> · <code>qty_count</code></li>
                  </ul>
                  <p style={{ margin: '4px 0' }}>
                    <strong>Operators</strong>: <code>+ - * / ^ % = &lt;&gt; &lt; &gt; &lt;= &gt;=</code>
                  </p>
                  <p style={{ margin: '4px 0' }}>
                    <strong>Functions</strong> (Excel-compatible):
                    IF, IFERROR, ROUND, ABS, AVERAGE, MIN, MAX, COUNT, SUM, AND, OR, NOT, …
                  </p>
                  <p style={{ margin: '4px 0' }}>
                    <strong>Note</strong>: row-wise aggregation (SUMPRODUCT, SUMIF over expressions)
                    is not yet supported. Aggregates are pre-computed per column.
                  </p>
                </div>
              </details>
            </>
          )}

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={labelStyle}>Label (optional)</span>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder='Defaults to "Total"'
              style={inputStyle}
            />
          </label>

          <div style={{
            padding: '8px 10px', borderRadius: 6,
            background: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border)',
            fontSize: '0.8425rem',
          }}>
            <div style={{
              color: 'var(--color-text-secondary)', fontSize: '0.8125rem',
              textTransform: 'uppercase', letterSpacing: '0.04em',
              marginBottom: 2,
            }}>
              Preview ({previewRows.length} row{previewRows.length === 1 ? '' : 's'})
            </div>
            <div style={{
              fontFamily: 'var(--font-mono, monospace)',
              color: preview.ok ? 'var(--color-text)' : '#ef4444',
            }}>
              {preview.display}
            </div>
          </div>
        </div>

        <div style={{
          padding: '10px 18px', borderTop: '1px solid var(--color-border)',
          display: 'flex', gap: 8, justifyContent: 'flex-end',
          background: 'var(--color-bg-secondary)',
        }}>
          {current && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => { onClear(); onClose() }}
              style={{ color: '#ef4444', marginRight: 'auto' }}
            >
              <Trash2 size={12} /> Remove total
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              const next: EmbedColumnTotal = kind === 'aggregate'
                ? { kind: 'aggregate', spec: agg, label: label || undefined }
                : { kind: 'expression', spec: expr.trim(), label: label || undefined }
              if (next.kind === 'expression' && !next.spec) return
              onSave(next)
              onClose()
            }}
            disabled={kind === 'expression' && !expr.trim()}
          >
            <Save size={12} /> Save
          </button>
        </div>
      </div>
    </div>
  )
  return createPortal(node, document.body)
}

const labelStyle: React.CSSProperties = {
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
}

const inputStyle: React.CSSProperties = {
  padding: '6px 8px',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 4,
  background: 'var(--color-input-bg)',
  color: 'var(--color-text)',
  fontSize: '0.8825rem',
  outline: 'none',
}
